// src/components/TaskPage/Modals/RequestTaskClosureModal.tsx

import React, { useState, useEffect } from "react";
import { Button, Modal, Form, Alert, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { Clock, MessageSquare, FileText, X, Calendar } from "lucide-react";
import { useAuth } from "../../../../firebase/AuthProvider";
import { TransactionReasonResponseDto } from "../../../../interfaces/adminRequests/ReasonTypes";
import { getReasonsByType } from "../../../../services/AdministrativeRequestServices/AdministrativeRequestService";
import { requestTaskExtension } from "../../../../services/AdministrativeRequestServices/TaskRequestService";
import { CreateAdministrativeTransactionRequest } from "../../../../interfaces/adminRequests/CreateAdministrativeTransactionRequest";
import "./RequestTaskExtensionModal.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { getDifferenceBetweenTwoDates } from "../../../../utilities/dateUtils";

const METHOD_OPTIONS = ["Phone call", "Text message", "Email", "Home visit"];
const IMPACT_OPTIONS = [
  "Participant waited longer to get the supports they need to live comfortably",
  "Services have been interrupted or not delivered when needed",
  "The participant missed out on being part of important planning conversations",
  "We didn't get a full or up-to-date understanding of what the participant needs right now",
  "The participant experienced unnecessary stress or confusion because of the delay",
  "There was a delay in coordinating care with other providers or supports",
  "The participant's goals or preferences are impacted",
];
const FOLLOW_UP_OPTIONS = [
  { label: "1", value: 1 },
  { label: "2", value: 2 },
  { label: "3 or more", value: 3 },
];
const TIME_NEED_OPTIONS = [
  { label: "+7 days", value: 7 },
  { label: "+14 days", value: 14 },
  { label: "+21 days", value: 21 },
  { label: "+30 days", value: 30 },
];

type FormData = {
  selectedReasonId: number | null;
  comment: string;
  firstAttemptDate: string;
  followUpCount: number;
  followUpMethods: string[];
  participantNotified: boolean | null;
  impactOnParticipant: string[];
  timeNeeded: number;
};

interface RequestTaskExtensionModalProps {
  show: boolean;
  onHide: () => void;
  taskId: number;
  onSuccess: () => void;
}

const RequestTaskExtensionModal: React.FC<RequestTaskExtensionModalProps> = ({
  show,
  onHide,
  taskId,
  onSuccess,
}) => {
  const { currentUser } = useAuth();
  const [reasons, setReasons] = useState<TransactionReasonResponseDto[]>([]);
  const [formData, setFormData] = useState<FormData>({
    selectedReasonId: null,
    comment: "",
    firstAttemptDate: "",
    followUpCount: 0,
    followUpMethods: [],
    participantNotified: null,
    impactOnParticipant: [],
    timeNeeded: 0,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingReasons, setIsLoadingReasons] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    const fetchReasons = async () => {
      if (!currentUser || !show) return;
      setIsLoadingReasons(true);
      try {
        const token = await currentUser.getIdToken();
        const result = await getReasonsByType(token, "EXTENSION");
        if ("error" in result) {
          setReasons([]);
          toast.error(`Error loading reasons: ${result.error}`);
        } else {
          setReasons(result);
        }
      } catch (err: any) {
        toast.error("Failed to load extension reasons.");
        console.error("Error fetching extension reasons:", err);
      } finally {
        setIsLoadingReasons(false);
      }
    };

    if (show) {
      setFormData({
        selectedReasonId: null,
        comment: "",
        firstAttemptDate: "",
        followUpCount: 0,
        followUpMethods: [],
        participantNotified: null,
        impactOnParticipant: [],
        timeNeeded: 0,
      });
      setErrors([]);
    }

    fetchReasons();
  }, [show, currentUser]);

  const toggleArrayValue = (key: keyof FormData, value: string) => {
    setFormData((prev) => {
      const arr = prev[key] as unknown as string[];
      if (arr.includes(value)) {
        return { ...prev, [key]: arr.filter((v) => v !== value) } as FormData;
      } else {
        return { ...prev, [key]: [...arr, value] } as FormData;
      }
    });
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value, type } = e.target as HTMLInputElement;
    setFormData((prev) => {
      if (name === "selectedReasonId") {
        const n = parseInt(value, 10);
        return { ...prev, selectedReasonId: Number.isNaN(n) ? null : n };
      }
      if (name === "followUpCount" || name === "timeNeeded") {
        const n = parseInt(value, 10) || 0;
        return { ...prev, [name]: n } as FormData;
      }
      if (name === "participantNotified") {
        if (value === "yes") return { ...prev, participantNotified: true };
        if (value === "no") return { ...prev, participantNotified: false };
        return { ...prev, participantNotified: null };
      }
      return { ...prev, [name]: value } as FormData;
    });
  };

  const getDateDiffError = () => {
    // Error check if the firstAttempDate exceeded more than 1 past year 
    if(!formData?.firstAttemptDate) return null;
     
    const attemptDate : Date = new Date(formData?.firstAttemptDate);
    const currentDate : Date = new Date();
    const {days} = getDifferenceBetweenTwoDates(attemptDate , currentDate);

    if(days >= 365) {
      return "Selected date must be within the past 1 year.";
    }

    return null;
  }

  const validate = (): string[] => {
    const errs: string[] = [];

    // Note formdata.comment is not a mandatory field.
    if (!formData.selectedReasonId)
      errs.push("Reason for extension is required.");
    if (!formData.firstAttemptDate)
      errs.push("Date of first contact attempt is required.");
    if (!formData.followUpCount || formData.followUpCount <= 0)
      errs.push("Follow-up count is required.");
    if (formData.followUpMethods.length === 0)
      errs.push("At least one contact method is required.");
    if (formData.participantNotified === null)
      errs.push("Participant notified selection is required.");
    if (!formData.timeNeeded || formData.timeNeeded <= 0)
      errs.push("Time needed selection is required.");
    if (formData.impactOnParticipant.length === 0)
      errs.push("At least one impact on participant is required.");

    return errs;
  };

  const isFormValid = (): boolean => {
    return validate().length === 0;
  };

  const handleSubmit = async () => {
    if (!currentUser) return;

    let validationErrors = validate();
    const dateDiffError : string | null = getDateDiffError();

    if(dateDiffError){
      validationErrors = [...validationErrors , dateDiffError];
    }

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors([]);
    setIsSubmitting(true);

    const payload: CreateAdministrativeTransactionRequest = {
      transactionReasonId: formData.selectedReasonId as number,
      transactionReasonText: formData.comment.trim(),
      contactEffort: {
        firstAttemptDate: formData.firstAttemptDate,
        followUpCount: formData.followUpCount,
        followUpMethods: formData.followUpMethods,
        participantNotified: !!formData.participantNotified,
        impactOnParticipant: formData.impactOnParticipant,
        timeneed: formData.timeNeeded,
      },
    };

    try {
      const token = await currentUser.getIdToken();
      const result = await requestTaskExtension(token, taskId, payload);
      if (result.success) {
         toast.success(result.message);
        onSuccess();
        onHide();
    } else {
          toast.error(result.message);       
      }
    } catch (err: any) {
      console.error("Submit extension error:", err);
      toast.error( err?.message || "Failed to submit extension request.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      className="myextesion task-extension-modal"
    >
      <div className="task-extension-modal__wrapper">
        <div className="modal-activity-reject-header ">
          <h2 className="modal-activity-reject-title flex gap-[0.5rem] items-center">
            <Clock
              size={14}
              className="task-extension-modal__section-icon mb-[2px]"
            />{" "}
            Request Task Extension
          </h2>
          <button
            className="modal-activity-reject-close"
            onClick={onHide}
            type="button"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className="modal-activity-reject-body">
          {isLoadingReasons && (
            <div className="task-extension-modal__loading">
              <Spinner animation="border" size="sm" /> Loading reasons...
            </div>
          )}

          {errors.length > 0 && (
            <Alert variant="danger" className="task-extension-modal__alert">
              <ul className="task-extension-modal__error-list">
                {errors.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            </Alert>
          )}

          <div className="form-group">
            <label className="form-label-reject">
              Reason for extension{" "}
              <span className="task-extension-modal__required">*</span>
            </label>
            <select
              className="form-select bg-[#F3F3F5]"
              name="selectedReasonId"
              value={formData.selectedReasonId ?? ""}
              onChange={handleInputChange}
              disabled={isSubmitting || isLoadingReasons}
            >
              <option value="">Select a reason</option>
              {reasons.map((reason) => (
                <option key={reason.id} value={reason.id}>
                  {reason.reasonText}
                </option>
              ))}
            </select>
          </div>

          <div className="task-extension-modal__section">
            <div className="task-extension-modal__section-header">
              <Calendar
                size={11}
                className="task-extension-modal__section-icon"
              />

              <span className="task-extension-modal__section-title">
                Contact Effort
              </span>
              <span className="task-extension-modal__required">*</span>
            </div>

            <div className="task-extension-modal__row">
              <div className="task-extension-modal__col">
                <label className="form-label-reject">
                  Last contact attempt
                </label>
                <div
                  style={{
                    background: "rgb(255, 255, 255)",
                    display: "flex",
                    width: "100%",
                    maxWidth: "100%",
                    padding: "0 0.5625rem",
                    alignItems: "center",
                    alignSelf: "stretch",
                    position: "relative",
                    height: "2.25rem",
                    minHeight: "2.25rem",
                    boxSizing: "border-box",
                    borderRadius: "0.6rem",
                    cursor: "pointer",
                  }}
                  onClick={(e) => {
                    const input = e.currentTarget.querySelector('input[type="date"]') as HTMLInputElement;
                    if (input && !input.disabled) {
                      input.focus();
                      input.showPicker?.();
                    }
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="20"
                    viewBox="0 0 18 20"
                    fill="none"
                    style={{
                      width: "1.125rem",
                      height: "1.25rem",
                      flexShrink: 0,
                      marginRight: "0.5625rem",
                      pointerEvents: "none",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M13 1V2H5V1C5 0.45 4.55 0 4 0C3.45 0 3 0.45 3 1V2H2C0.89 2 0.01 2.9 0.01 4L0 18C0 19.1 0.89 20 2 20H16C17.1 20 18 19.1 18 18V4C18 2.9 17.1 2 16 2H15V1C15 0.45 14.55 0 14 0C13.45 0 13 0.45 13 1Z"
                      fill="#C9C9C9"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M15 18H3C2.45 18 2 17.55 2 17V7H16V17C16 17.55 15.55 18 15 18Z"
                      fill="white"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M13 11H10C9.45 11 9 11.45 9 12V15C9 15.55 9.45 16 10 16H13C13.55 16 14 15.55 14 15V12C14 11.45 13.55 11 13 11Z"
                      fill="#C9C9C9"
                    />
                  </svg>
                  <input
                    type="date"
                    name="firstAttemptDate"
                    value={formData.firstAttemptDate}
                    onChange={handleInputChange}
                    disabled={isSubmitting || isLoadingReasons}
                    min={(() => {
                      const oneYearAgo = new Date();
                      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
                      return oneYearAgo.toISOString().split("T")[0];
                    })()}
                    max={new Date().toISOString().split("T")[0]}
                    style={{
                      flex: 1,
                      border: "none",
                      outline: "none",
                      background: "transparent",
                      padding: "0",
                      margin: "0",
                      fontSize: "1rem",
                      color: "#212121",
                      cursor: "pointer",
                      lineHeight: "2.25rem",
                      height: "100%",
                      verticalAlign: "middle",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!e.currentTarget.disabled) {
                        e.currentTarget.showPicker?.();
                      }
                    }}
                  />
                </div>
              </div>
              <div className="task-extension-modal__col">
                <label className="form-label-reject">Follow-up attempts</label>
                <select
                  className="form-select bg-white !important"
                  name="followUpCount"
                  value={formData.followUpCount || ""}
                  onChange={handleInputChange}
                  disabled={isSubmitting || isLoadingReasons}
                >
                  <option value="">Select count</option>
                  {FOLLOW_UP_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="task-extension-modal__field">
              <label className="form-label-reject form-label-reject--methods">
                Methods used{" "}
                <span className="task-extension-modal__required">*</span>
              </label>
              <div className="task-extension-modal__checkbox-grid">
                {METHOD_OPTIONS.map((m) => (
                  <label
                    key={m}
                    className="task-extension-modal__checkbox-item"
                  >
                    <input
                      type="checkbox"
                      className="task-extension-modal__checkbox"
                      checked={formData.followUpMethods.includes(m)}
                      onChange={() => toggleArrayValue("followUpMethods", m)}
                      disabled={isSubmitting || isLoadingReasons}
                    />
                    <span className="task-extension-modal__checkbox-label">
                      {m}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="task-extension-modal__field">
            <label className="form-label-reject">
              Was the participant notified of the upcoming deadline?{" "}
              <span className="task-extension-modal__required">*</span>
            </label>
            <select
              className={`form-select ${formData.participantNotified === null ? "placeholder-selected" : ""}`}
              name="participantNotified"
              value={
                formData.participantNotified === null
                  ? ""
                  : formData.participantNotified
                  ? "yes"
                  : "no"
              }
              onChange={handleInputChange}
              disabled={isSubmitting || isLoadingReasons}
            >
              <option value="">Select</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
          <div className="task-extension-modal__form-group">
            <label className="form-label-reject">
              How has this delay affected the participant's services or
              well-being?{" "}
              <span className="task-extension-modal__required">*</span>
            </label>
            <select
              className={`form-select ${!formData.impactOnParticipant[0] ? "placeholder-selected" : ""}`}
              name="impactOnParticipant"
              value={formData.impactOnParticipant[0] || ""}
              onChange={e =>
                setFormData(prev => ({
                  ...prev,
                  impactOnParticipant: e.target.value ? [e.target.value] : [],
                }))
              }
              disabled={isSubmitting || isLoadingReasons}
            >
              <option value="">Select impact</option>
              {IMPACT_OPTIONS.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          <div className="task-extension-modal__form-group">
            <label className="form-label-reject">
              How much more time is needed?{" "}
              <span className="task-extension-modal__required">*</span>
            </label>
            <select
              className={`form-select ${!formData.timeNeeded ? "placeholder-selected" : ""}`}
              name="timeNeeded"
              value={formData.timeNeeded || ""}
              onChange={handleInputChange}
              disabled={isSubmitting || isLoadingReasons}
            >
              <option value="">Select time needed</option>
              {TIME_NEED_OPTIONS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          <div className="task-extension-modal__form-group task-extension-modal__form-group--with-divider">
            <div className="task-extension-modal__comment-header">
              <MessageSquare
                size={14}
                className="task-extension-modal__comment-icon mb-[2px]"
              />
              <label className="form-label-reject mb-0 !important">
                Additional Comments{" "}
                <span className="task-extension-modal__required">*</span>
              </label>
            </div>
            <textarea
              className="task-extension-modal__textarea"
              rows={3}
              name="comment"
              placeholder="Provide any additional details..."
              value={formData.comment}
              onChange={handleInputChange}
              disabled={isSubmitting || isLoadingReasons}
            />
          </div>
        </div>

        <div className="task-extension-modal__footer ml-2">
          <button
            className="btn btn-secondary cancel-btn w-[50%]"
            onClick={onHide}
            disabled={isSubmitting}
            type="button"
          >
            Cancel
          </button>
          <button
            className="btn btn-primary submit-btn w-[50%]"
            onClick={handleSubmit}
            // disabled={isSubmitting || isLoadingReasons || !isFormValid()}
            type="button"
          >
            {isSubmitting ? (
              <>
                <Spinner
                  animation="border"
                  size="sm"
                  className="task-extension-modal__spinner"
                />
                Submitting...
              </>
            ) : (
              "Submit Request"
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default RequestTaskExtensionModal;
